import type { RegisterOptions } from "react-hook-form";

// bcrypt truncates input at 72 bytes, so the auth-service sets maxPasswordLength: 72. Login accepts
// existing passwords down to 8 chars (better-auth's default); new passwords must meet the stronger
// signup minimum (10 chars, plus character variety — see the tiers). Keep the signup minimum in sync
// with minPasswordLength in nodejs/apps/auth-service/src/auth/better-auth.factory.ts.
export const PASSWORD_LOGIN_MIN_LENGTH = 8;
export const PASSWORD_SIGNUP_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 72;

// Base rules shared by every password field: required + the bcrypt-driven max length. The two
// concrete rule sets below extend this — they diverge only on the *minimum*.
const basePasswordFieldRules = {
  required: { value: true, message: "Password is required" },
  maxLength: {
    value: PASSWORD_MAX_LENGTH,
    message: `Password must be at most ${PASSWORD_MAX_LENGTH} characters`,
  },
} satisfies RegisterOptions;

// Login: an existing password, so only the length bounds apply (never re-graded for strength).
export const passwordFieldRules = {
  ...basePasswordFieldRules,
  minLength: {
    value: PASSWORD_LOGIN_MIN_LENGTH,
    message: `Password must be at least ${PASSWORD_LOGIN_MIN_LENGTH} characters`,
  },
} satisfies RegisterOptions;

// New password (register / reset): the Medium strength gate replaces the min-length check (strength
// already requires ≥10 chars). The validator is message-less — the strength meter shows the
// specifics — and gates react-hook-form's `isValid`, hence the submit button. `deps` re-checks the
// confirm field when the password changes so the match stays in sync.
export const newPasswordFieldRules = {
  ...basePasswordFieldRules,
  validate: { strength: (value: string) => isPasswordStrongEnough(value) },
} satisfies RegisterOptions;

// Rules for a "confirm password" field. Takes a getter for the password to compare against
// (react-hook-form's getValues), since both fields live in the same form. The match check is skipped
// while this field is empty so it doesn't flash "do not match" the moment you start typing the
// password (the password field's `deps` re-validates this one); `required` still catches it on submit.
export const confirmPasswordRules = (getPassword: () => string) =>
  ({
    required: { value: true, message: "Please confirm your password" },
    validate: (value: string) => !value || value === getPassword() || "Passwords do not match",
  }) satisfies RegisterOptions;

// --- Strength scoring -------------------------------------------------------
// Scored purely on length + character variety (lowercase / uppercase / number / symbol), so it's a
// few lines rather than a dependency. The register/reset forms gate submit on reaching Medium (see
// newPasswordFieldRules); the meter renders the score + the "how to strengthen it" hints.

const CHARACTER_CLASSES: { regex: RegExp; label: string }[] = [
  { regex: /[a-z]/, label: "a lowercase letter" },
  { regex: /[A-Z]/, label: "an uppercase letter" },
  { regex: /[0-9]/, label: "a number" },
  { regex: /[^a-zA-Z0-9]/, label: "a symbol" },
];

function countCharacterClasses(password: string): number {
  return CHARACTER_CLASSES.filter(({ regex }) => regex.test(password)).length;
}

export const PASSWORD_STRENGTH_LABELS = ["Too weak", "Weak", "Medium", "Strong"] as const;

// Medium is the tier the register/reset forms gate submit on. It requires all four character classes
// (matching the policy of the Auth0 login it replaced); named here so the tier table and the "how to
// strengthen it" hints stay in sync.
const MEDIUM_MIN_CLASSES = 4;

// Strength tiers, strongest first. A password takes the highest tier whose class-count AND length
// are both met; if none match it's "Too weak" (score 0). Medium and Strong differ only by length.
const PASSWORD_STRENGTH_TIERS = [
  { score: 3, label: "Strong", minClasses: 4, minLength: 12 },
  {
    score: 2,
    label: "Medium",
    minClasses: MEDIUM_MIN_CLASSES,
    minLength: PASSWORD_SIGNUP_MIN_LENGTH,
  },
  { score: 1, label: "Weak", minClasses: 1, minLength: PASSWORD_LOGIN_MIN_LENGTH },
] as const;

export type PasswordStrength = {
  // 0 = Too weak … 3 = Strong; also the number of filled bars in the meter.
  score: number;
  label: string;
};

export function getPasswordStrength(password: string): PasswordStrength {
  const classes = countCharacterClasses(password);
  const tier = PASSWORD_STRENGTH_TIERS.find(
    (t) => classes >= t.minClasses && password.length >= t.minLength,
  );
  return tier ? { score: tier.score, label: tier.label } : { score: 0, label: "Too weak" };
}

// The register/reset forms disable submit until the password reaches this score (Medium). This is a
// client-side UX gate; the server enforces its own minimum length (minPasswordLength).
export const MIN_PASSWORD_STRENGTH_SCORE = 2;

export function isPasswordStrongEnough(password: string): boolean {
  return getPasswordStrength(password).score >= MIN_PASSWORD_STRENGTH_SCORE;
}

// The concrete things still missing for the password to clear the Medium gate — a longer password
// and/or more character variety. Returns [] once the gate is met. Drives the meter's guidance.
export function getPasswordStrengthHints(password: string): string[] {
  const hints: string[] = [];

  if (password.length < PASSWORD_SIGNUP_MIN_LENGTH) {
    hints.push(`Use at least ${PASSWORD_SIGNUP_MIN_LENGTH} characters`);
  }

  const missing = CHARACTER_CLASSES.filter(({ regex }) => !regex.test(password));
  const classesNeeded = MEDIUM_MIN_CLASSES - (CHARACTER_CLASSES.length - missing.length);
  if (classesNeeded > 0) {
    const noun = classesNeeded === 1 ? "type" : "types";
    hints.push(
      `Add ${classesNeeded} more character ${noun}: ${missing.map((c) => c.label).join(", ")}`,
    );
  }

  return hints;
}
